'use client'

import { cn } from '@/utilities/ui'
import React, { useEffect, useState, useSyncExternalStore } from 'react'

import { Media } from '@/components/Media'
import { getMediaUrl } from '@/utilities/getMediaUrl'

import type { Media as MediaType } from '@/payload-types'

export type HeroSlideshowSlide = {
  heading: string
  image: number | MediaType
  caption?: string | null
  id?: string | null
}

type Props = {
  slides: HeroSlideshowSlide[]
  autoplay?: boolean | null
  intervalMs?: number | null
  firstHeading?: string | null
  thirdHeading?: string | null
  /** Silent looping video shown instead of the slide photos once it starts playing. */
  coverVideo?: number | MediaType | null
  className?: string
}

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

export const HomepageHeroSlideshowClient: React.FC<Props> = ({
  slides,
  autoplay = true,
  intervalMs = 5000,
  firstHeading,
  thirdHeading,
  coverVideo,
  className,
}) => {
  const [index, setIndex] = useState(0)
  // False during server render and for visitors who ask for reduced motion: both keep the photos.
  const videoAllowed = useSyncExternalStore(
    subscribeReducedMotion,
    () => !window.matchMedia(REDUCED_MOTION).matches,
    () => false,
  )
  const [videoPlaying, setVideoPlaying] = useState(false)
  const coverVideoSrc =
    coverVideo && typeof coverVideo === 'object' && coverVideo.url ? getMediaUrl(coverVideo.url) : ''

  const len = slides?.length ?? 0

  // Reset to the first slide when the slide count changes (render-phase adjustment).
  const [prevLen, setPrevLen] = useState(len)
  if (prevLen !== len) {
    setPrevLen(len)
    setIndex(0)
  }

  useEffect(() => {
    if (!autoplay || len < 2) return
    const ms = Math.max(2000, intervalMs || 5000)
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % len)
    }, ms)
    return () => window.clearInterval(id)
  }, [autoplay, intervalMs, len])

  if (!slides?.length) return null

  const hasHeroText =
    Boolean(firstHeading?.trim()) ||
    slides.some((s) => Boolean(s.heading?.trim())) ||
    Boolean(thirdHeading?.trim())

  return (
    // Section: px-[10px] matches WP. pb-28/pb-36 leaves room for card that overflows below.
    <section
      className={cn('w-full pb-[120px]', className)}
      aria-roledescription="carousel"
    >
      <div className="relative w-full">
        {/* Background slideshow — position absolute, full height, no opacity dimming */}
        <div className="relative min-h-[70vh] w-full overflow-hidden">
          {slides.map((slide, i) => {
            const isActive = i === index
            return (
              <div
                key={slide.id ?? i}
                className={cn(
                  'absolute inset-0 transition-opacity duration-500',
                  isActive && !videoPlaying ? 'z-[1] opacity-100' : 'z-0 opacity-0',
                )}
                aria-hidden={!isActive}
              >
                {slide.image && typeof slide.image === 'object' ? (
                  <Media
                    className="relative h-full w-full"
                    fill
                    imgClassName="object-cover"
                    priority={i === 0}
                    resource={slide.image}
                  />
                ) : null}
              </div>
            )
          })}
          {coverVideoSrc && videoAllowed ? (
            // The photos stay underneath until the first frame plays, so a slow or blocked video
            // (Low Power Mode, data saver) leaves the normal slideshow on screen.
            <video
              aria-hidden
              autoPlay
              className={cn(
                'absolute inset-0 z-[2] h-full w-full object-cover transition-opacity duration-500',
                !videoPlaying && 'invisible',
              )}
              loop
              muted
              onPlaying={() => setVideoPlaying(true)}
              playsInline
              preload="auto"
              src={coverVideoSrc}
            />
          ) : null}
        </div>

        {/* White card — absolutely positioned overlapping the bottom edge, matching WP */}
        {hasHeroText ? (
          <div
            className={cn(
              'absolute left-1/2 top-full z-10 w-full -translate-x-1/2 -translate-y-[50%] px-[10px]',
              'md:w-[calc(50vw-10px)] md:-translate-y-[78%] lg:-translate-y-[70%] md:px-0 lg:w-[502px] 2xl:w-[570px]',
            )}
          >
            <div
              className="flex w-full flex-col items-center justify-center gap-0 pt-[18px] pb-[26px] rounded-[9px] px-[10px] text-center"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.99)',
                boxShadow: 'rgba(0, 0, 0, 0.5) 0px 0px 10px 0px',
              }}
            >
              {/* "Remodel Your" — 34px/300/lh:40.8 mobile | 72px/300/lh:86.4 desktop */}
              {firstHeading?.trim() ? (
                <p className="font-light leading-[40.8px] text-[34px] text-[rgb(29,29,27)] md:text-[72px] md:leading-[86.4px]">
                  {firstHeading.trim()}
                </p>
              ) : null}

              {/* Dynamic word — 34px/500/lh:40.8 mobile | 72px/500/lh:86.4 desktop, teal, Ms Madi */}
              {slides.length > 0 ? (
                <div
                  className="relative grid w-full place-items-center"
                  style={{ minHeight: '41px' }}
                  aria-live="polite"
                >
                  {slides.map((slide, i) => {
                    const line = slide.heading?.trim()
                    if (!line) return null
                    return (
                      <span
                        key={slide.id ?? `h-${i}`}
                        className="col-start-1 row-start-1 max-w-full font-ms-madi text-[34px] leading-[40.8px] text-[rgb(0,155,171)] md:text-[72px] md:leading-[86.4px]"
                        style={{
                          fontWeight: 500,
                          zIndex: i === index ? 10 : 0,
                          opacity: i === index ? 1 : 0,
                          transform: i === index ? 'scale(1)' : 'scale(1.3)',
                          transition:
                            i === index
                              ? 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1100ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                              : 'opacity 700ms ease-out',
                        }}
                        aria-hidden={i !== index}
                      >
                        {line}
                      </span>
                    )
                  })}
                </div>
              ) : null}

              {/* "With Craft" — 34px/300/lh:40.8 mobile | 72px/300/lh:86.4 desktop */}
              {thirdHeading?.trim() ? (
                <p className="font-light leading-[40.8px] text-[34px] text-[rgb(29,29,27)] md:text-[72px] md:leading-[86.4px]">
                  {thirdHeading.trim()}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}
